<template>
  <div></div>
</template>

<script>
import { createSlider } from "@typeform/embed";
import { mapActions, mapGetters } from "vuex";
import "@typeform/embed/build/css/slider.css";
import moment from "moment-timezone";

export default {
  name: "BaseScoreDialog",
  data() {
    return {
      slider: undefined,
      currentScore: 0,
      scoring: false,
      focuses: [],
      programId: null,
      shownScoreIds: [],
      pendingScores: [],
    };
  },

  computed: {
    ...mapGetters("scores", ["scores"]),
    ...mapGetters("forms", ["focusFormResponses"]),
    ...mapGetters("users", ["getEmail", "user"]),
  },

  watch: {
    scores(newVal) {
      if (!this.scoring) this.create();
    },
  },
  async mounted() {
    await this.startPolling({ apolloClient: this.$apollo });
  },

  methods: {
    ...mapActions("scores", ["startPolling"]),
    ...mapActions("forms", ["getFocusFormResponses"]),
    ...mapActions("schedules", ["fetchCoacheeSessions"]),
    open(score) {
      if (this.user?.trips?.[0]?.status === "canceled") return;
      if (score._id && score.path) {
        // Avoid storing duplicate score IDs to keep lookups efficient
        if (!this.shownScoreIds.includes(score._id)) {
          this.shownScoreIds.push(score._id);
        }
        const hidden = {
          score_id: score._id,
          environment: process.env.NODE_ENV,
          env: process.env.NODE_ENV,
          email: this.getEmail,
          program_id: this.programId,
        };
        if (score.score === "csat" && score.type === "coachee") {
          hidden.session_id = score.reference;
        }
        if (this.focuses.length) {
          for (let i = 0; i < this.focuses.length; i++) {
            hidden[`focus_${i + 1}`] = this.focuses[i];
          }
        }
        if (score.sessionData) {
          const { coachee_name, coachee_lastname, organization, session_date } =
            score.sessionData;
          if (coachee_name) hidden.coachee_name = coachee_name;
          if (coachee_lastname) hidden.coachee_lastname = coachee_lastname;
          if (organization) hidden.organization = organization;
          // Only set session_date if it can be parsed as a valid date by moment
          if (session_date) {
            const parsedSessionDate = moment(session_date);
            if (parsedSessionDate.isValid()) {
              hidden.session_date = parsedSessionDate.format("DD MMM YYYY");
            }
          }
        }
        const slider = createSlider(score.path, {
          autoresize: true,
          onClose: this.close,
          hidden,
          position: "left",
        });
        this.slider = slider;
        slider.open();
      }
    },

    async getProgramId() {
      try {
        // Coachee: Has programs ids, use the last one
        if (this.user?.programs?.length) {
          return this.user.programs[this.user.programs.length - 1]._id;
        }

        // Coach: Has no programs ids, use score.reference -> get session -> session.program
        const session = await this.fetchSessionById(this.pendingScores[0].reference);
        if (!session) return null;

        return session[0]?.program?._id;
      } catch (error) {
        console.error("Error fetching program ID:", error);
        return null;
      }
    },

    async fetchSessionById(id) {
      try {
        const session = await this.fetchCoacheeSessions({
          apolloClient: this.$apollo,
          data: {
            filter: {
              _id: id,
            },
          },
        });
        return session;
      } catch (error) {
        console.error(`Error fetching session with ID ${id}:`, error);
        return null;
      }
    },

    async create() {
      // Prevent concurrent create() executions that could race on component state
      if (this.scoring) {
        return;
      }

      // Mark scoring as active before any asynchronous operations to act as a guard flag
      this.scoring = true;

      try {
        const pendingScores = this.scores.filter(
          (s) => s && s._id && s.path && !this.shownScoreIds.includes(s._id)
        );

        if (!pendingScores.length) {
          // No scores to handle, reset scoring flag so future updates can trigger create()
          this.scoring = false;
          return;
        }

        this.pendingScores = pendingScores;
        this.programId = await this.getProgramId();

        if (this.programId) {
          await this.getFocusFormResponses({
            apolloClient: this.$apollo,
            programId: this.programId,
            userId: this.user._id,
          });

          if (this.focusFormResponses.length) {
            this.focuses = this.focusFormResponses.map((focus) => focus.label);
          }
        }

        this.currentScore = 0;
        this.open(this.pendingScores[this.currentScore]);
      } catch (error) {
        console.error(error);
        // Ensure scoring flag is cleared on error to avoid blocking future attempts
        this.scoring = false;
      }
    },

    async close() {
      this.currentScore++;
      if (this.pendingScores[this.currentScore])
        this.open(this.pendingScores[this.currentScore]);
      else this.scoring = false;
    },
  },
};
</script>

<style lang="scss" scoped></style>
